import React, { useMemo }         from 'react';
import { ICreditUnion }           from '@typings/accountManagement';
import { TDataTableRow }          from '@typings/components';
import {
  EAccountStatus,
  EAccountPage,
}                                 from '@enums/accountManagement';
import { EAppPage }               from '@enums/common';
import { ACCOUNTS_TABLE_HEADERS } from '@modules/accountManagement/utils/tableHeaders';
import { DataTable }              from '@components/index';

interface IProps {
  accounts            : ICreditUnion[];
  fetchAccountsStatus : boolean;
}

const accountsStatusOrder: EAccountStatus[] = [
  EAccountStatus.Active,
  EAccountStatus.Cancelled,
  EAccountStatus.Prospect,
  EAccountStatus.ReviewingContract,
];

const ACCOUNT_STATUS_NAME: Record<EAccountStatus, string> = {
  [EAccountStatus.Unknown]           : '',
  [EAccountStatus.Active]            : 'Active Suppliers',
  [EAccountStatus.Cancelled]         : 'Canceled',
  [EAccountStatus.Prospect]          : 'Prospect',
  [EAccountStatus.ReviewingContract] : 'Reviewing Contract',
};

const getSectionRow = (
  section: EAccountStatus,
): TDataTableRow => [{
  value   : `${section}`,
  title   : ACCOUNT_STATUS_NAME[section],
  section : true,
}];

const getItemRows = (
  section  : number,
  accounts : ICreditUnion[],
): TDataTableRow[] =>
  accounts
    .filter(
      (account: ICreditUnion): boolean =>
        account.status === section,
    )
    .reduce(
      (itemRows: TDataTableRow[], account: ICreditUnion): TDataTableRow[] => {
        const row: TDataTableRow = [
          {
            value : `${account.status}`,
            meta  : true,
          },
          {
            value : account.name,
            link  : `${EAppPage.AccountManagement}/${account.cuid}/${EAccountPage.Edit}`,
            align : 'left',
          },
          {
            value : 'Credit Union',
            align : 'center',
          },
          {
            value : account.city,
            align : 'left',
          },
          {
            value : account.state,
            align : 'center',
          },
        ];

        itemRows.push(row);

        return itemRows;
      }, [],
    );

const getTableRows = (
  accounts: ICreditUnion[],
): any =>
  accountsStatusOrder.reduce(
    (tableRows: TDataTableRow[], section: EAccountStatus): TDataTableRow[] => {
      const sectionRow = getSectionRow(section);

      tableRows.push(sectionRow);

      const itemRows = getItemRows(section, accounts);

      tableRows.push(...itemRows);

      return tableRows;
    },
    [],
  );

export const AccountSearchTable = ({
  accounts,
  fetchAccountsStatus,
}: IProps): JSX.Element => {
  const tableRows = useMemo(
    (): TDataTableRow[] => getTableRows(accounts),
    [accounts],
  );

  return (
    <DataTable
      headers  = {ACCOUNTS_TABLE_HEADERS}
      rows     = {tableRows}
      loading  = {fetchAccountsStatus}
      maxwidth = "1280px"
    />
  );
};
